import api from '../config/api';

/**
 * Order Service - Handles order-related API calls
 */
class OrderService {
  /**
   * Calculate shipping rate (single provider — kept for backward compat)
   */
  async calculateRate(rateData) {
    const response = await api.post('/orders/calculate-rate', rateData);
    return response.data;
  }

  /**
   * Calculate rates from ALL providers simultaneously.
   * Returns { courierOptions: [...] } sorted by price.
   * Provider names are hidden — each option has a composite ID: "provider::courierId"
   */
  async calculateAllRates(rateData) {
    const response = await api.post('/orders/calculate-rate-all', rateData);
    return response.data;
  }

  /**
   * Create new order
   */
  async createOrder(orderData) {
    const response = await api.post('/orders', orderData);
    return response.data;
  }

  /**
   * Get user orders
   */
  async getUserOrders(filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.deliveryPartner) params.append('deliveryPartner', filters.deliveryPartner);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.skip) params.append('skip', filters.skip);
    if (filters.type) params.append('type', filters.type);
    if (filters.orderType) params.append('orderType', filters.orderType);
    if (filters.search) params.append('search', filters.search);

    const response = await api.get(`/orders?${params.toString()}`);
    return response.data;
  }

  /**
   * Get orders (alias for getUserOrders)
   */
  async getOrders(filters = {}) {
    return this.getUserOrders(filters);
  }

  /**
   * Get delivered orders eligible for reversal
   */
  async getReverseEligibleOrders(filters = {}) {
    const params = new URLSearchParams();
    if (filters.deliveryPartner) params.append('deliveryPartner', filters.deliveryPartner);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.skip) params.append('skip', filters.skip);
    if (filters.search) params.append('search', filters.search);
    const response = await api.get(`/orders/reverse-eligible?${params.toString()}`);
    return response.data;
  }

  /**
   * Get order by ID
   */
  async getOrderById(orderId) {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  }

  /**
   * Track order
   */
  async trackOrder(orderId) {
    const response = await api.get(`/orders/${orderId}/track`);
    return response.data;
  }

  /**
   * Public track order (no authentication)
   */
  async publicTrackOrder(trackingId) {
    const response = await api.get(`/orders/track/public/${trackingId}`);
    return response.data;
  }

  /**
   * Raise pickup for this order (handles all partners).
   */
  async requestPickup(orderId, otp = null) {
    const response = await api.post(`/orders/${orderId}/pickup`, { otp });
    return response.data;
  }

  /**
   * Alias for backward compatibility.
   */
  async requestNimbusPickup(orderId) {
    return this.requestPickup(orderId);
  }

  /**
   * Download shipping label PDF for this order (handles all partners).
   * The backend will return either:
   *   - { type: 'url', url: '...' }  — stored S3 label URL (open in new tab)
   *   - A PDF blob                   — streamed from Carrier API
   */
  async downloadLabel(orderId, { inline = false } = {}) {
    // Inline mode (Delhivery International): open the PDF in a browser tab
    // instead of saving to Downloads. The tab must be opened synchronously
    // here — while the click is still a user gesture — because the label
    // request takes several seconds and popup blockers would refuse a
    // window.open after the await below.
    const labelTab = inline ? window.open('', '_blank') : null;
    if (labelTab) {
      labelTab.document.write('<title>Shipping label</title><p>Loading label…</p>');
      labelTab.document.close();
    }
    try {
      const response = await api.post(
        `/orders/${orderId}/label`,
        {},
        { responseType: 'blob' }
      );

      const contentType = response.headers['content-type'] || '';

      // ✅ Backend returned JSON: either { type: 'url', url: '...' } or an error
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);

        // Stored label URL — open in new tab
        if (json.type === 'url' && json.url) {
          if (labelTab) {
            labelTab.location.href = json.url;
          } else {
            window.open(json.url, '_blank', 'noopener,noreferrer');
          }
          return { success: true, url: json.url };
        }

        // Stored base64 label (Delhivery International) — decode and download
        if (json.type === 'base64' && json.data) {
          const byteChars = atob(json.data);
          const byteNums = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            byteNums[i] = byteChars.charCodeAt(i);
          }
          const byteArr = new Uint8Array(byteNums);
          const blob = new Blob([byteArr], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          if (labelTab) {
            labelTab.location.href = url;
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            return { success: true, awb: json.awb, inline: true };
          }
          const a = document.createElement('a');
          a.href = url;
          a.download = `shipping-label-${json.awb || 'delhivery-intl'}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true, awb: json.awb };
        }

        // Error JSON
        if (json.message) throw new Error(json.message);
        return json;
      }

      // ✅ Backend returned a raw PDF blob — trigger download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      if (labelTab) {
        labelTab.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        return { success: true, inline: true };
      }
      const a = document.createElement('a');
      a.href = url;
      let filename = 'shipping-label.pdf';
      const cd = response.headers['content-disposition'];
      if (cd && cd.includes('filename=')) {
        const m = cd.match(/filename="?([^";\n]+)"?/i);
        if (m) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      if (labelTab) {
        try { labelTab.close(); } catch {}
      }
      const status = err.response?.status;
      const raw = err.response?.data;
      if (raw instanceof Blob) {
        const text = await raw.text();
        let json;
        try { json = JSON.parse(text); } catch { throw new Error(text || `Request failed (${status})`); }
        const msg = json.message || json.error || text;
        const e = new Error(msg);
        e.status = status;
        e.payload = json;
        throw e;
      }
      throw err;
    }
  }

  /**
   * Download the Delhivery International (StarFleet) invoice PDF for this order.
   * Same mechanics as downloadLabel: backend returns either
   *   - { type: 'url', url: '...' }  — stored invoice URL (open in new tab)
   *   - A raw PDF blob               — fetched live from StarFleet
   */
  async downloadInvoice(orderId, { inline = false } = {}) {
    // Inline mode (Delhivery International): open the PDF in a browser tab
    // instead of saving to Downloads. The tab must be opened synchronously
    // here — while the click is still a user gesture — because the invoice
    // request takes several seconds and popup blockers would refuse a
    // window.open after the await below.
    const invoiceTab = inline ? window.open('', '_blank') : null;
    if (invoiceTab) {
      invoiceTab.document.write('<title>Invoice</title><p>Loading invoice…</p>');
      invoiceTab.document.close();
    }
    try {
      const response = await api.post(
        `/orders/${orderId}/invoice`,
        {},
        { responseType: 'blob' }
      );

      const contentType = response.headers['content-type'] || '';

      // Backend returned JSON: either { type: 'url', url: '...' } or an error
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);

        // Stored invoice URL — open in new tab
        if (json.type === 'url' && json.url) {
          if (invoiceTab) {
            invoiceTab.location.href = json.url;
          } else {
            window.open(json.url, '_blank', 'noopener,noreferrer');
          }
          return { success: true, url: json.url };
        }

        // Error JSON
        if (json.message) throw new Error(json.message);
        return json;
      }

      // Backend returned a raw PDF blob — open inline or trigger download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      if (invoiceTab) {
        invoiceTab.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        return { success: true, inline: true };
      }
      const a = document.createElement('a');
      a.href = url;
      let filename = 'invoice.pdf';
      const cd = response.headers['content-disposition'];
      if (cd && cd.includes('filename=')) {
        const m = cd.match(/filename="?([^";\n]+)"?/i);
        if (m) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      if (invoiceTab) {
        try { invoiceTab.close(); } catch {}
      }
      const status = err.response?.status;
      const raw = err.response?.data;
      if (raw instanceof Blob) {
        const text = await raw.text();
        let json;
        try { json = JSON.parse(text); } catch { throw new Error(text || `Request failed (${status})`); }
        const msg = json.message || json.error || text;
        const e = new Error(msg);
        e.status = status;
        e.payload = json;
        throw e;
      }
      throw err;
    }
  }

  /**
   * Cancel shipment for this order (handles both Nimbus and SKart backend-side).
   */
  async cancelShipment(orderId) {
    const response = await api.post(`/orders/${orderId}/cancel`);
    return response.data;
  }

  /**
   * Alias for backward compatibility.
   */
  async cancelNimbusShipment(orderId) {
    return this.cancelShipment(orderId);
  }
  
  /**
   * Cancel a pickup request.
   */
  async cancelPickupRequest(pickupId) {
    const response = await api.post(`/pickup-requests/${pickupId}/cancel`);
    return response.data;
  }
}

const orderService = new OrderService();
export default orderService;
